// App Card Component
// Displays individual app information in both grid and list views
// Adapts layout based on view mode and includes all required app data

import { RevoltButton } from '../../../components';
import { TemplateBadge } from '../../../components';
import { StatusCluster } from './StatusCluster';
import {
  canShowWorkspaceButton,
  canOpenWorkspace,
  canOpenPlayspace,
} from '../../../core/utils/appAccessUtils';
import { envUtils } from '../../../core/utils';

export function AppCard({
  project,
  viewMode = 'grid',
  onToggleFavorite,
  onOpenWorkspace,
  onOpenPlayspace,
  onOpenSettings,
  onOpenContextMenu,
  manager
}) {
  const {
    id,
    name,
    template,
    appType,
    isFavorited,
    createdAt,
    modifiedAt,
    isOwner,
    isAdmin,
    ownerName
  } = project;

  // Extract app color for tinting
  const appColor = project.settings?.appColor;
  const cardTintStyle = appColor ? {
    '--app-color-r': parseInt(appColor.slice(1, 3), 16),
    '--app-color-g': parseInt(appColor.slice(3, 5), 16),
    '--app-color-b': parseInt(appColor.slice(5, 7), 16),
    '--app-color-alpha': appColor.length === 9
      ? parseInt(appColor.slice(7, 9), 16) / 255
      : 0.15 // Default alpha if no alpha specified
  } : {};

  const statusMode = 'idle';
  const isPublic = !!project.isPublic;
  const isFavorite = !!isFavorited;

  const isDesktop = envUtils.device.isDesktopApp();
  const showWorkspace = canOpenWorkspace({ isOwner, isAdmin }, project, { isDesktop });
  const showPlayspace = canOpenPlayspace(project, { isDesktop });
  const showSettings = canShowWorkspaceButton({ isOwner, isAdmin });

  const handleWorkspaceClick = (e) => {
    e.stopPropagation();
    onOpenWorkspace(id);
  };

  const handlePlayspaceClick = (e) => {
    e.stopPropagation();
    onOpenPlayspace(id);
  };

  const handleSettingsClick = (e) => {
    e.stopPropagation();
    onOpenSettings(id);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onOpenContextMenu) {
      onOpenContextMenu(project, e);
    }
  };

  if (viewMode === 'list') {
    return (
      <div
        className={`project-card list-card is-desktop ${isFavorite ? 'favorited' : ''}`}
        style={cardTintStyle}
        onContextMenu={handleContextMenu}
      >
        <div className="list-card-body">
          <div className="list-card-left">
            <StatusCluster
              status={statusMode}
              isPublic={isPublic}
              isFavorite={isFavorite}
            />
            <h3 className="list-card-name with-owner">
              {name}
              <span
                className={`project-owner-icon ${isOwner ? 'owned' : 'shared'}`}
                title={isOwner ? 'You own this app' : `Owned by ${ownerName || 'Unknown'}`}
              >
                <i className={isOwner ? 'fa-solid fa-circle-check' : 'fa-regular fa-user'}></i>
              </span>
            </h3>
            <div className="list-card-dates-inline">
              <span className="date-label-inline">
                Created: {manager.formatDate(createdAt)}
              </span>
              <span className="date-separator">•</span>
              <span className="date-label-inline">
                Modified: {manager.formatDate(modifiedAt)}
              </span>
            </div>
            <div className="list-card-badge">
              <TemplateBadge template={template} appType={appType} flip />
            </div>
          </div>
          <div className="list-card-actions">
            {showSettings && (
              <RevoltButton
                variant="ghost"
                size="sm"
                as="a"
                href={`/app-settings/${id}`}
                data-navigation-button
                onClick={async (e) => {
                  e.preventDefault();
                  await handleSettingsClick(e);
                }}
                onContextMenu={(e) => e.stopPropagation()}
                aria-label="Open settings"
              >
                <i className="fa-solid fa-gear"></i>
              </RevoltButton>
            )}
            {showWorkspace && (
              <RevoltButton
                variant="accent"
                size="sm"
                as="a"
                href={`/workspace/${id}`}
                data-navigation-button
                onClick={async (e) => {
                  e.preventDefault();
                  await handleWorkspaceClick(e);
                }}
                onContextMenu={(e) => e.stopPropagation()}
              >
                Workspace
              </RevoltButton>
            )}
            {showPlayspace && (
              <RevoltButton
                variant="ghost"
                size="sm"
                as="a"
                href={`/playspace/${id}`}
                data-navigation-button
                onClick={async (e) => {
                  e.preventDefault();
                  await handlePlayspaceClick(e);
                }}
                onContextMenu={(e) => e.stopPropagation()}
              >
                Playspace
              </RevoltButton>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Grid view (default)
  return (
    <div
      className={`project-card grid-card is-desktop ${isFavorite ? 'favorited' : ''}`}
      style={cardTintStyle}
      onContextMenu={handleContextMenu}
    >
      <div className="project-card-header-minimal">
        <h3 className="project-card-title">
          {name}
          <span
            className={`project-owner-icon ${isOwner ? 'owned' : 'shared'}`}
            title={isOwner ? 'You own this app' : `Owned by ${ownerName || 'Unknown'}`}
          >
            <i className={isOwner ? 'fa-solid fa-circle-check' : 'fa-regular fa-user'}></i>
          </span>
        </h3>
        <StatusCluster
          status={statusMode}
          isPublic={isPublic}
          isFavorite={isFavorite}
        />
          </div>

      <div className="project-card-badge-row">
        <div className="project-card-badge-left">
          <span className="date-label">Modified: {manager.formatDate(modifiedAt)}</span>
        </div>
        <TemplateBadge template={template} appType={appType} flip />
      </div>

      <div className="project-card-footer-minimal">
        <div className="project-card-footer-left-actions">
          {showSettings && (
            <RevoltButton
              variant="ghost"
              size="sm"
              as="a"
              href={`/app-settings/${id}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await handleSettingsClick(e);
              }}
              onContextMenu={(e) => e.stopPropagation()}
              aria-label="Open settings"
            >
              <i className="fa-solid fa-gear"></i>
            </RevoltButton>
          )}
        </div>
        <div className="project-card-footer-right-actions">
          {showPlayspace && (
            <RevoltButton
              variant="ghost"
              size="sm"
              as="a"
              href={`/playspace/${id}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await handlePlayspaceClick(e);
              }}
              onContextMenu={(e) => e.stopPropagation()}
            >
              Playspace
            </RevoltButton>
          )}
          {showWorkspace && (
            <RevoltButton
              variant="accent"
              size="sm"
              as="a"
              href={`/workspace/${id}`}
              data-navigation-button
              onClick={async (e) => {
                e.preventDefault();
                await handleWorkspaceClick(e);
              }}
              onContextMenu={(e) => e.stopPropagation()}
            >
              Workspace
            </RevoltButton>
          )}
        </div>
      </div>
    </div>
  );
}

